import React, { useEffect, useRef, useState } from "react";

interface Option {
  label: string;
  value: string;
  disabled?: boolean;
}

interface AutocompleteProps {
  id?: string;
  value: string;
  options: Option[];
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  className?: string;
  error?: string;
}

const normalize = (value: string) =>
  value
    .toLowerCase();

const Autocomplete: React.FC<AutocompleteProps> = ({
  id,
  value,
  options,
  onChange,
  onBlur,
  placeholder = "Sélectionner une option",
  className = "",
  error,
}) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find(
    (option) => option.value === value
  );

  const filteredOptions =
    query.trim() === ""
      ? options
      : options.filter((option) =>
          normalize(option.label).includes(normalize(query))
        );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [query]);

  const handleSelect = (option: Option) => {
    if (option.disabled) return;

    onChange(option.value);
    setQuery("");
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && (e.key === "ArrowDown" || e.key === "Enter")) {
      setIsOpen(true);
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();

      setHighlightedIndex((current) =>
        current < filteredOptions.length - 1 ? current + 1 : 0
      );
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();

      setHighlightedIndex((current) =>
        current > 0 ? current - 1 : filteredOptions.length - 1
      );
    }

    if (e.key === "Enter") {
      e.preventDefault();

      const option = filteredOptions[highlightedIndex];

      if (option) {
        handleSelect(option);
      }
    }

    if (e.key === "Escape") {
      setIsOpen(false);
      setQuery("");
    }
  };

  return (
    <div
      ref={containerRef}
      className={`relative ${className}`}
    >
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={isOpen ? query : selectedOption?.label ?? ""}
          placeholder={placeholder}
          onFocus={() => {
            setIsOpen(true);
            setQuery("");
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          onBlur={onBlur}
          className={`
            w-full
            h-12
            rounded-xl
            border
            bg-white
            px-4
            pr-10
            outline-none
            transition
            ${
              error
                ? "border-red-300 text-red-500"
                : "border-gray-200 focus:border-blue-500"
            }
          `}
        />

        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            setIsOpen((current) => !current);
            inputRef.current?.focus();
          }}
          className="absolute right-0 top-0 flex h-12 w-12 items-center justify-center text-gray-400"
        >
          <svg
            className={`h-5 w-5 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg">
          {filteredOptions.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500">
              Aucun résultat trouvé
            </div>
          ) : (
            filteredOptions.map((option, index) => (
              <button
                key={option.value}
                type="button"
                disabled={option.disabled}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(option)}
                className={`
                  flex
                  w-full
                  items-center
                  px-4
                  py-2.5
                  text-left
                  text-sm
                  transition
                  ${
                    option.disabled
                      ? "cursor-not-allowed text-gray-400"
                      : index === highlightedIndex
                      ? "bg-blue-50 text-blue-700"
                      : "text-gray-700 hover:bg-gray-50"
                  }
                `}
              >
                <span className="truncate">
                  {option.label}
                </span>

                {option.value === value && (
                  <svg
                    className="ml-auto h-5 w-5 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </button>
            ))
          )}
        </div>
      )}

      {error && (
        <p className="mt-1 text-sm text-red-500">
          {error}
        </p>
      )}
    </div>
  );
};

export default Autocomplete;